import { useLayoutEffect, useRef, useState } from 'react';
import loadScript from 'load-script';

/**
 * This hook is based from https://github.com/mehdisadeghi/react-mathjax-preview
 * modified as service support init mathjax once
 * @param {string} sciprt - script to load mathjax via cdn
 * @param {Object} config
 */

const baseConfig = {
  showMathMenu: true,
  tex2jax: {
    inlineMath: [
      ['$', '$'],
      ['\\(', '\\)'],
    ],
  },
  skipStartupTypeset: true,
};

const defaultMathJaxscript =
  'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.6/MathJax.js?config=TeX-MML-AM_HTMLorMML';

const loadMathJax = (script, config, { onCompleted, onError } = {}) => {
  loadScript(script, (err, script) => {
    if (err) {
      console.error('failed to load mathjax script');
      typeof onError === 'function' && onError();
    } else {
      window.MathJax.Hub.Config({ ...baseConfig, ...config });
      typeof onCompleted === 'function' && onCompleted(window.MathJax);
    }
  });
};

const transform = (node, { onCompleted, MathJax = window?.MathJax } = {}) =>
  MathJax.Hub.Queue([
    'Typeset',
    MathJax.Hub,
    node,
    () => typeof onCompleted === 'function' && onCompleted(),
  ]);

const transformMath = (
  node,
  { script = defaultMathJaxscript, config, onCompleted, onTransform, onTransformError } = {}
) => {
  if (!node) {
    typeof onTransformError === 'function' && onTransformError();
    return;
  }

  typeof onTransform === 'function' && onTransform();

  if (!window?.MathJax) {
    loadMathJax(script, config, {
      onCompleted: (MathJax) => transform(node, { onCompleted, MathJax: MathJax }),
      onError: onTransformError,
    });
  } else {
    transform(node, { onCompleted });
  }
};

/**
 * @function useMathJax
 *
 * This is useMathJax Hook,
 * function which is transform node from latex syntax into mathematic symbols
 *
 * becareful when use this hook inside of component's loop,
 * if no mathjax instance on first mount,it can cause unnessessary scritp load.
 * solved by use this hook by parse ref in upper level,MathJax can transform node and child at once.
 *
 * @param {*} dependency useLayoutEffect dependency,this hook'll re transform latex math syntax on dependency change
 *
 * @example
 * const  {
 *  ref,
 *  isTransforming
 * } = useMathJax();
 */
const useMathJax = (dependency, { script, config } = {}) => {
  const ref = useRef();
  const [transformState, setTransformState] = useState(TRANSFORM_STATE.TRANSFORMING);
  /**
   * transformMath should be run before screen updated
   * more info: https://daveceddia.com/useeffect-vs-uselayouteffect/
   */
  useLayoutEffect(() => {
    transformMath(ref.current, {
      script,
      config,
      onCompleted: () => {
        setTransformState(TRANSFORM_STATE.SUCCESS);
      },
      onTransformError: () => {
        setTransformState(TRANSFORM_STATE.ERROR);
      },
    });
    return () => {
      setTransformState(TRANSFORM_STATE.TRANSFORMING);
    };
  }, [config, dependency, script]);

  return {
    /**
     * @property {Object} ref react's ref for passing to dom, which to be render as math
     */
    ref,
    /**
     * @property {String} isTransforming to be true when transformState is TRANSFORMING
     */
    isTransforming: transformState === TRANSFORM_STATE.TRANSFORMING,
  };
};

const TRANSFORM_STATE = {
  TRANSFORMING: 'TRANSFORMING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};
export default useMathJax;
